import { useState, useEffect, useRef } from 'react';
import useToast from '@sdk/common/toast/useToast';
import getErrorForToast from '@sdk/api/utils/getErrorForToast';
import useSlingApi from '@sdk/api/useSlingApi';
import { ShiftFeedbackFromApi, ShiftFeedbackRating } from '@sdk/timesheets/types/ShiftFeedback';
import gettext from '@sdk/lang/gettext';
import { getBaseSequenceId } from '@sdk/timesheets/helpers/utils';
import { TimesheetProjection } from '@sdk/timesheets/types/TimesheetProjection';

type Props = {
  isVisible: boolean;
  shiftId: string;
  projection: TimesheetProjection;
  close: () => void;
};

export const useShiftFeedbackNotificationModal = ({
  isVisible,
  shiftId,
  projection,
  close,
}: Props) => {
  const commentRef = useRef(null);
  const api = useSlingApi();
  const { showErrorToast, showSuccessToast } = useToast();
  const [ratingProvided, setRatingProvided] = useState(false);
  const shiftFeedbackRef = useRef<ShiftFeedbackFromApi>();
  const [feedbackComment, setFeedbackComment] = useState('');

  const clockInBaseSequenceId = getBaseSequenceId(projection.id);

  useEffect(() => {
    setRatingProvided(false);
    shiftFeedbackRef.current = undefined;
    setFeedbackComment('');
  }, [isVisible]);

  useEffect(() => {
    if (ratingProvided) {
      commentRef.current?.focus();
    }
  }, [ratingProvided]);

  const onRatingClicked = async (ratingOption: ShiftFeedbackRating) => {
    try {
      const { data } = await api.timesheets.addShiftFeedback(
        shiftId,
        clockInBaseSequenceId,
        ratingOption
      );
      shiftFeedbackRef.current = data;
      setRatingProvided(true);
    } catch (e) {
      showErrorToast(getErrorForToast(e));
    }
  };

  const updateFeedback = async () => {
    try {
      if (feedbackComment) {
        await api.timesheets.updateShiftFeedback(
          shiftFeedbackRef.current.id,
          shiftFeedbackRef.current.rating,
          feedbackComment
        );
      }
      onDismiss();
    } catch (e) {
      showErrorToast(getErrorForToast(e));
    }
  };

  const onDismiss = () => {
    if (shiftFeedbackRef.current) {
      showSuccessToast(gettext('Your feedback has been submitted'));
    }

    close();
  };

  return {
    onDismiss,
    ratingProvided,
    commentRef,
    feedbackComment,
    setFeedbackComment,
    updateFeedback,
    shiftFeedbackRef,
    onRatingClicked,
  };
};
